/**
 * First-visit language prompt + header language button.
 *
 * - Shows the prompt once per browser (localStorage), never inside the theme
 *   editor unless the section setting allows it.
 * - Pre-selects the visitor's browser language when the store offers it.
 * - Submitting uses Shopify's own localization form, which redirects to the
 *   same page in the chosen language (e.g. /de/...).
 * - With "remember choice" on, a visitor who explicitly chose a language and
 *   later arrives from outside the site on the default-language URL (no
 *   language prefix) is sent to that language. Links that already carry a
 *   language (/fr/...) are respected. Switching languages inside the site
 *   (footer selector, header button) updates the memory; dismissing the
 *   prompt only marks it as seen.
 * - Injects a "Language" button first in Cascade's header meta area and turns
 *   the login link into a user icon, so the header reads Language, User, Cart,
 *   Buy now without editing the theme's header markup.
 */
(function () {
  var KEY = 'luminous:language';      // explicitly chosen language
  var SEEN = 'luminous:language-seen'; // prompt already shown in this browser
  var EARTH =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="12" r="10"/><path d="M21.54 15H17a2 2 0 0 0-2 2v4.54"/>' +
    '<path d="M7 3.34V5a3 3 0 0 0 3 3a2 2 0 0 1 2 2c0 1.1.9 2 2 2a2 2 0 0 0 2-2c0-1.1.9-2 2-2h3.17"/>' +
    '<path d="M11 21.95V18a2 2 0 0 0-2-2a2 2 0 0 1-2-2v-1a2 2 0 0 0-2-2H2.05"/></svg>';
  var USER =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 3.6-7 8-7s8 3 8 7"/></svg>';

  var dialog = document.querySelector('[data-lang-prompt]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  var cfg = {};
  try { cfg = JSON.parse(dialog.querySelector('[data-lang-prompt-config]').textContent); } catch (e) {}
  var form = dialog.querySelector('form');
  var select = dialog.querySelector('[data-lang-select]');
  if (!form || !select) return;

  var options = Array.prototype.slice.call(select.options);
  var available = options.map(function (o) { return o.value; });
  var current = cfg.current || select.value;

  function read(k) { try { return localStorage.getItem(k || KEY); } catch (e) { return null; } }
  function write(v, k) { try { localStorage.setItem(k || KEY, v); } catch (e) {} }
  function markSeen() { write('1', SEEN); }

  function sameOriginReferrer() {
    if (!document.referrer) return false;
    try { return new URL(document.referrer).origin === location.origin; } catch (e) { return false; }
  }

  function matchBrowserLanguage() {
    var wanted = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language || ''])
      .map(function (l) { return String(l).toLowerCase(); });
    var lower = available.map(function (a) { return a.toLowerCase(); });
    for (var i = 0; i < wanted.length; i++) {
      var exact = lower.indexOf(wanted[i]);
      if (exact > -1) return available[exact];
      var base = wanted[i].split('-')[0];
      for (var j = 0; j < lower.length; j++) {
        if (lower[j].split('-')[0] === base) return available[j];
      }
    }
    return null;
  }

  function dismiss() {
    markSeen();
    if (dialog.open) dialog.close();
  }

  function openPrompt() {
    select.value = current;
    if (!dialog.open) dialog.showModal();
    select.focus();
  }

  form.addEventListener('submit', function (event) {
    markSeen();
    if (select.value === current) {
      event.preventDefault();
      dialog.close();
      return;
    }
    write(select.value);
  });
  dialog.querySelectorAll('[data-close]').forEach(function (btn) {
    btn.addEventListener('click', dismiss);
  });
  dialog.addEventListener('cancel', markSeen);
  dialog.addEventListener('click', function (event) {
    if (event.target === dialog) dismiss();
  });

  // Floating button (optional): reopen the prompt at any time.
  var opener = document.querySelector('[data-lang-open]');
  if (opener) opener.addEventListener('click', openPrompt);

  // Header: Language button first, then the login link as a user icon.
  var meta = document.querySelector('.header__meta .flex') || document.querySelector('.header__meta');
  if (cfg.headerButton && meta && !meta.querySelector('.lang-header-button')) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 't--header-navigation header__link lang-header-button color--primary-text hv--div--border-bottom--transparent color--header-active font-size--m bg--transparent inline-block pointer m0 px1';
    btn.setAttribute('aria-label', cfg.headerTitle || 'Change language');
    btn.title = cfg.headerTitle || 'Change language';
    btn.innerHTML = EARTH + '<span class="lang-header-button__name">' + (cfg.headerLabel || '') + '</span>';
    btn.addEventListener('click', openPrompt);
    meta.insertBefore(btn, meta.firstChild);
  }
  if (cfg.accountIcon && meta) {
    var account = meta.querySelector('a[href*="customer_authentication"], a[href*="/account"]');
    if (account && !account.classList.contains('lang-account-icon')) {
      var label = account.textContent.trim();
      account.classList.add('lang-account-icon');
      account.setAttribute('aria-label', label);
      account.title = label;
      account.innerHTML = USER;
    }
  }

  var remembered = read();
  var inEditor = !!(window.Shopify && window.Shopify.designMode);

  // Keep the memory in step with in-site language switches (footer/header).
  if (remembered && sameOriginReferrer() && remembered !== current) {
    write(current);
    remembered = current;
  }

  // Return visit from outside on the default-language URL: go to the chosen language.
  if (remembered && !inEditor && cfg.rememberChoice && cfg.isDefaultLocale && !sameOriginReferrer() &&
      remembered !== current && available.indexOf(remembered) > -1) {
    select.value = remembered;
    form.submit();
    return;
  }

  if (read(SEEN) || remembered) return;

  if (inEditor && !cfg.showInEditor) return;

  if (cfg.preselectBrowserLanguage) {
    var guess = matchBrowserLanguage();
    if (guess) select.value = guess;
  }

  dialog.showModal();
  select.focus();
})();
